import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useWebSocketStore } from "../../stores/websocketStore";
import { Box, Button, Paper, Typography, IconButton } from "@mui/material";
import TooltipComponent from "../common/Tooltip"
import { ExitToApp, CopyAllOutlined, GroupAddOutlined, LoginOutlined } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import LobbyAlerts from "./LobbyAlerts";
import {
    LobbyMatchNightProvider,
} from "./LobbyMatchNightContext.jsx";
import LobbyMatchNightPanel, {
    LobbyMatchNightShowButton,
} from "./LobbyMatchNightPanel.jsx";
import RefragPromoNotice from "../common/RefragPromoNotice.jsx";
import utils from "../../universal/utils";
import { fetchMembershipStatus } from "../../redux/actions/catalogActions";
import { buildReferralInviteLink } from "../../universal/referralLinks";
import { pushToast } from "../../stores/toastStore";
import { notifyIfCannotSendFriendRequest } from "../../utils/friendRequests";
import useQueueEstimate from "./useQueueEstimate";
import {
    primeMatchNotificationSounds,
    waitForMatchNotificationSoundsReady,
} from "../../universal/matchNotificationSounds";
import LobbyParticles from "./LobbyParticles.jsx";
import LobbyPlayerStage from "./LobbyPlayerStage";
import CustomMapSubscriptionDialog from "./CustomMapSubscriptionDialog";
import OuroCodeDialog from "./OuroCodeDialog.jsx";
import SteamPrivacyCheckDialog from "./SteamPrivacyCheckDialog.jsx";
import SmallerMatchIntroDialog from "./SmallerMatchIntroDialog.jsx";
import useSmallerMatchIntro from "./useSmallerMatchIntro";
import {
    shouldShowOuroCode,
    recordOuroCodeAck,
} from "./ouroCode";
import LobbySearchingStatus from "./LobbySearchingStatus";
import LobbyMatchReadyStatus from "./LobbyMatchReadyStatus";
import LobbyMapVoteStatus from "./LobbyMapVoteStatus";
import LobbyMatchmakingDock from "./LobbyMatchmakingDock";
import ConfirmDialog from "../common/ConfirmDialog.jsx";
import {
    openLobbySettingsPanel,
} from "../../stores/lobbySettingsPanelStore";
import {
    maskInviteCode,
    normalizeLocationId,
    readConfirmedCustomMapWorkshopIds,
    writeConfirmedCustomMapWorkshopIds,
} from "./lobbyPageUtils";
import {
    MAIN_SECTION_MIN_HEIGHT_SX,
    MOBILE_NAV_HEIGHT,
} from "../layout/layoutConstants";
import TopBarActionButton, {
    TOPBAR_ACTION_ICON_SX,
} from "../layout/TopBarActionButton";
import {
    LOBBY_EXIT_BUTTON_BLOCKED_SX,
    LOBBY_EXIT_BUTTON_SX,
    CORNER_BRACKET_SX,
    CORNER_BRACKET_TL_SX,
    CORNER_BRACKET_TR_SX,
    CORNER_BRACKET_BL_SX,
    CORNER_BRACKET_BR_SX,
    SEARCHING_BRACKET_ANIMATION,
    MOBILE_NAV_MQ,
    SHORT_VIEWPORT_MQ,
} from "./lobbyPageStyles";
import useLobbyPublicMeta from "./useLobbyPublicMeta";
import useLobbyQueueStatus from "./useLobbyQueueStatus";
import useLobbyRealtimeRouter from "./useLobbyRealtimeRouter";
import useLobbySocialBootstrap from "./useLobbySocialBootstrap";
import useLobbyViewModel from "./useLobbyViewModel";
import { toast } from "../../universal/toast";
import {
    listOfflinePartyMembers,
    buildOfflinePartyQueueToast,
} from "../../utils/lobbyPresence";
import { AnimatePresence, motion } from "framer-motion";

const MotionBox = motion(Box);

const { fetchJsonWithDiagnostics } = utils;

export default function LobbyPage({ actions, catalog }) {

    const dispatch = useDispatch();
    const [referralCopied, setReferralCopied] = useState(false);
    const [inviteCode, setInviteCode] = useState(null);
    const [inviteExpiresAt, setInviteExpiresAt] = useState(null);
    const [inviteLoading, setInviteLoading] = useState(false);
    const { sendOwnerAction, subscribe, clientId } = useWebSocketStore();
    const friendsRef = useRef();

    const history = useHistory();
    const basePath = utils.getEdgeBasePath();

    const {
        user,
        lobbyMapsSelected = [],
        lobbyWingmanMapsSelected = [],
        selectedServerLocations = [],
        steamData = {},
        matchmakingOptions,
        lobbyState,
        pendingRequests = [],
        friends = [],
        currentLobby = null,
        currentLobbyCode = null,
        matchAcceptDialogState = false,
        mapVoteState = false,
        queueBlockedModal = {
            open: false,
            reason: null,
            message: null,
            blockedMembers: [],
        },
        queueStatus = null,
        matchmakingStarted = false,
        lobbyCodeNotFound = false,
        lobbyFull = false,
    } = catalog;
    const resolvedUserSteamId =
        user?.steamId || user?.steamId64 || user?.steam64Id || null;

    const { discordInviteUrl, founderProgress, referralCode } =
        useLobbyPublicMeta({ basePath, user });
    const [customMapSubscriptionDialog, setCustomMapSubscriptionDialog] =
        useState({
            open: false,
            maps: [],
        });
    const [pendingQueueStartMode, setPendingQueueStartMode] = useState(null);
    const [ouroCodeDialogOpen, setOuroCodeDialogOpen] = useState(false);
    const [pendingOuroCodeMode, setPendingOuroCodeMode] = useState(null);
    const pendingOuroCodeModeRef = useRef(null);
    const suppressNextOuroCodePromptRef = useRef(false);
    const ouroCodeAcceptLockRef = useRef(false);

    // Pre-download all match notification sounds as soon as the Lobby mounts
    // so they are ready before any match-found dialog or redirect fires.
    useEffect(() => {
        primeMatchNotificationSounds();
        waitForMatchNotificationSoundsReady().catch(() => {
            // Non-fatal: if preload fails, play helpers still lazy-load on demand.
        });
    }, []);

    useEffect(() => {
        if (catalog?.user?.userId) {
            dispatch(fetchMembershipStatus(catalog.user.userId));
        }
    }, [catalog?.user?.userId, dispatch]);

    // Auto-generate invite code for existing lobbies that don't have one
    useEffect(() => {
        if (currentLobbyCode && !inviteCode && !inviteLoading) {
            handleGenerateInviteCode();
        }
    }, [currentLobbyCode]);

    const {
        isLeader,
        lobbyMembers,
        selfMember,
        otherMembers,
        isLobbyFull,
        canEditLobbySettings,
        lobbyOptionsToDisplay,
        queueMode: lobbyQueueMode,
        lobbyMapsToDisplay,
        lobbyServerLocationsToDisplay,
        hasSelectedMaps,
        hasSelectedServerLocations,
        hasRequiredQueueSelections,
        partyMemberUserIds,
        selectedCustomWorkshopMaps,
        mapsRecapSummary,
        serverLocationsRecapSummary,
        matchmakingRecapSummary,
        shouldBlockActions,
        canFlipCards,
        pendingBySlot,
    } = useLobbyViewModel({
        catalog,
        user,
        currentLobby,
        currentLobbyCode,
        matchmakingOptions,
        lobbyMapsSelected,
        lobbyWingmanMapsSelected,
        selectedServerLocations,
        lobbyState,
        matchmakingStarted,
        friends,
    });
    const queueEstimate = useQueueEstimate({
        basePath,
        enabled: Boolean(user?.userId && hasRequiredQueueSelections),
        selectedMaps: lobbyMapsToDisplay,
        selectedServerLocations: lobbyServerLocationsToDisplay,
        matchmakingOptions: lobbyOptionsToDisplay,
        country: selfMember?.country || user?.country || null,
        age: selfMember?.age || user?.age || null,
        gender: selfMember?.gender || user?.gender || null,
        elo: lobbyQueueMode === "wingman" ? (user?.eloWingman ?? 1000) : (user?.elo || null),
        queueMode: lobbyQueueMode,
        partyMemberUserIds,
    });

    const handleStopSearch = () => {
        actions.lobbyStopSearch({ userId: user?.userId });
        actions.clearQueueStatus();

        if (currentLobbyCode && lobbyMembers.length > 0) {
            sendOwnerAction({
                messageId: "matchmaking-stopped",
                lobbyCode: currentLobbyCode,
                leaderUserId: user.userId,
                memberUserIds: lobbyMembers
                    .filter((m) => m.userId !== user.userId)
                    .map((m) => m.userId),
            });
        }
    };

    const handleSmallerMatchOptIn = () => {
        if (user?.userId) {
            actions.lobbySmallerMatchOptIn({ userId: user.userId });
        }
    };

    const handleSmallerMatchDecline = () => {
        if (user?.userId) {
            actions.lobbySmallerMatchDecline({ userId: user.userId });
        }
    };

    const isMapVoteView = Boolean(mapVoteState?.matchId);
    const isMatchReadyView =
        Boolean(matchAcceptDialogState?.matchId) && !isMapVoteView;
    const isLockedMatchView = isMatchReadyView || isMapVoteView;
    const isSearchingView =
        !isLockedMatchView &&
        (lobbyState === "searching" || Boolean(matchmakingStarted));
    const isStatusOverlayView = isSearchingView || isLockedMatchView;
    
    const canCancelSearch = Boolean(isLeader || !currentLobbyCode);
    const isSearchingParty =
        Boolean(currentLobbyCode) && lobbyMembers.length > 1;
    const { open: smallerMatchIntroOpen, dismiss: dismissSmallerMatchIntro } =
        useSmallerMatchIntro({
            blocked:
                isStatusOverlayView ||
                ouroCodeDialogOpen ||
                Boolean(queueBlockedModal.open) ||
                Boolean(customMapSubscriptionDialog.open),
        });
    const displayLobbyCode = inviteCode || currentLobbyCode || "";
    const searchingAverageElo = useMemo(() => {
        const elos = (lobbyMembers || [])
            .map((member) => Number(member?.elo))
            .filter((elo) => Number.isFinite(elo));
        if (elos.length > 0) {
            return Math.round(
                elos.reduce((sum, elo) => sum + elo, 0) / elos.length
            );
        }
        const soloElo = Number(user?.elo);
        return Number.isFinite(soloElo) ? soloElo : null;
    }, [lobbyMembers, user?.elo]);

    const onAcceptMatch = () => {
        if (!matchAcceptDialogState?.matchId) return;
        actions.lobbyAcceptMatch({ matchId: matchAcceptDialogState.matchId });
        sendOwnerAction({
            messageId: "accept-match",
            matchId: matchAcceptDialogState.matchId,
            userId: user?.userId,
            clientId,
        });
    };

    const onVoteMap = (map) => {
        const matchId = mapVoteState?.matchId;
        if (!matchId || !map) return;
        const sent = sendOwnerAction({
            messageId: "cast-map-vote",
            matchId,
            map,
            userId: user?.userId,
            clientId,
        });
        if (sent) {
            actions.castMapVoteLocally({ matchId, map });
        }
    };

    const onDismissMapVote = () => {
        actions.closeMapVote();
    };

    const { queueClosedInfo, scheduleCountdown, isQueueEffectivelyClosed } =
        useLobbyQueueStatus({
            basePath,
            fetchJsonWithDiagnostics,
            lobbyState,
            onStopSearch: handleStopSearch,
            resolvedUserSteamId,
            routePath: history.location?.pathname,
            user,
        });

    useLobbySocialBootstrap({
        actions,
        friends,
        friendsRef,
        resolvedUserSteamId,
        user,
    });

    useLobbyRealtimeRouter({
        actions,
        catalog,
        friendsRef,
        handleGenerateInviteCode,
        history,
        resolvedUserSteamId,
        setInviteCode,
        setInviteExpiresAt,
        subscribe,
        user,
    });

    useEffect(() => {
        friendsRef.current = catalog.friends;
    }, [catalog.friends]);

    const { accountStatus = null } = user || {};
    const lobbyNoticeRefreshKey = [
        user?.phoneNumber || "",
        user?.phoneVerification?.status || "",
        user?.phoneVerification?.verifiedAt || "",
        user?.phoneVerification?.expiresAt || "",
    ].join("|");

    const onMapClicked = (name) => {
        actions.lobbyMapClicked({
            name,
            userId: user.userId,
            queueMode: lobbyQueueMode,
        });
    };

    const onServerLocationClicked = (locationId) => {
        actions.lobbyServerLocationClicked({ locationId, userId: user.userId });
    };

    const toggleAllServerLocationsSelection = (locationIds, allSelected) => {
        actions.toggleAllServerLocationsSelection({
            locationIds,
            userId: user.userId,
            allSelected,
        });
    };

    const toggleAllMapsSelection = (maps, allSelected) => {
        actions.toggleAllMapsSelection({
            maps,
            userId: user.userId,
            allSelected,
            queueMode: lobbyQueueMode,
        });
    };

    const handleLeaderMapUpdate = (mapName) => {
        actions.lobbyLeaderMapToggled({
            mapName,
            fallbackMaps: lobbyMapsToDisplay,
        });

        sendOwnerAction({
            messageId: "lobby-map-update",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            mapName,
            clientId,
        });
    };

    const handleLeaderToggleAllMaps = (maps, selectAll) => {
        actions.lobbyLeaderMapsToggleAll({ maps, selectAll });

        sendOwnerAction({
            messageId: "lobby-maps-toggle-all",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            maps,
            selectAll,
            clientId,
        });
    };

    const handleLeaderServerLocationUpdate = (locationId) => {
        actions.lobbyLeaderServerLocationToggled({
            locationId,
            fallbackLocations: lobbyServerLocationsToDisplay,
        });

        sendOwnerAction({
            messageId: "lobby-server-location-update",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            locationId,
            clientId,
        });
    };

    const handleLeaderToggleAllServerLocations = (locationIds, selectAll) => {
        const normalizedLocations = (locationIds || [])
            .map((locationId) => normalizeLocationId(locationId))
            .filter(Boolean);
        const nextLocations = selectAll ? normalizedLocations : [];

        actions.lobbyLeaderUpdatedServerLocations({
            lobbyCode: currentLobbyCode,
            lobbyServerLocationsSelected: nextLocations,
        });

        sendOwnerAction({
            messageId: "lobby-server-locations-toggle-all",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            locationIds: normalizedLocations,
            selectAll,
            clientId,
        });
    };

    const handleMatchmakingOptionChange = (settingData) => {
        actions.lobbyMatchmakingOptionChanged({
            name: settingData.name,
            enabled: settingData.enabled,
            value: settingData.value,
            hasSlider: settingData.hasSlider,
            listValue: settingData.listValue,
            userId: user.userId,
        });
    };

    const handleLeaderMatchmakingUpdate = (settingData) => {
        actions.lobbyLeaderMatchmakingOptionChanged({
            name: settingData.name,
            enabled: settingData.enabled,
            value: settingData.value,
            hasSlider: settingData.hasSlider,
            listValue: settingData.listValue,
        });

        sendOwnerAction({
            messageId: "lobby-options-update",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            name: settingData.name,
            enabled: settingData.enabled,
            value: settingData.value,
            hasSlider: settingData.hasSlider,
            listValue: settingData.listValue,
            clientId,
        });
    };

    const handleLobbyQueueModeSelect = (queueMode) => {
        const maxPlayers = queueMode === "wingman" ? 2 : 5;

        actions.lobbyQueueModeSwitched({
            lobbyCode: currentLobbyCode,
            queueMode,
            maxPlayers,
        });

        sendOwnerAction({
            messageId: "lobby-queue-mode-update",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            queueMode,
            clientId,
        });
    };

    const handleOpenLobbySettings = useCallback((tabId) => {
        actions?.setFriendSidebarOpen?.(false);
        openLobbySettingsPanel(tabId);
    }, [actions]);

    const handleSelectionError = (message) => {
        actions.showQueueErrorMessage(message);
    };

    const getUnconfirmedCustomWorkshopMaps = () => {
        if (!selectedCustomWorkshopMaps.length) return [];
        const confirmedWorkshopIds = readConfirmedCustomMapWorkshopIds();
        return selectedCustomWorkshopMaps.filter(
            (map) => !confirmedWorkshopIds.has(map.workshopItemId),
        );
    };

    const startQueueByMode = async (mode) => {
        if (mode === "solo") {
            await actions.lobbyAddPlayerToQueuePool({
                userId: user.userId,
                queueMode: lobbyQueueMode,
            });
            return;
        }

        if (mode === "lobby") {
            const queueStartedAt = await actions.lobbyAddPlayerToQueuePool({
                userId: user.userId,
                queueMode: lobbyQueueMode,
            });
            if (!queueStartedAt) {
                return;
            }

            if (currentLobbyCode && lobbyMembers.length > 0) {
                sendOwnerAction({
                    messageId: "lobby-matchmaking-started",
                    lobbyCode: currentLobbyCode,
                    leaderUserId: user.userId,
                    playerQueueActionStartTs: queueStartedAt,
                    memberUserIds: lobbyMembers
                        .filter((m) => m.userId !== user.userId)
                        .map((m) => m.userId),
                });
            }
        }
    };

    const guardQueueStartWithOuroCode = (mode) => {
        if (suppressNextOuroCodePromptRef.current) {
            suppressNextOuroCodePromptRef.current = false;
            guardQueueStartWithCustomMapConfirmation(mode);
            return;
        }

        if (shouldShowOuroCode()) {
            pendingOuroCodeModeRef.current = mode;
            setPendingOuroCodeMode(mode);
            setOuroCodeDialogOpen(true);
            return;
        }
        guardQueueStartWithCustomMapConfirmation(mode);
    };

    const handleAcceptOuroCode = () => {
        if (ouroCodeAcceptLockRef.current) {
            return;
        }
        ouroCodeAcceptLockRef.current = true;

        recordOuroCodeAck();
        suppressNextOuroCodePromptRef.current = true;
        setOuroCodeDialogOpen(false);

        const nextMode = pendingOuroCodeModeRef.current || pendingOuroCodeMode;
        pendingOuroCodeModeRef.current = null;
        setPendingOuroCodeMode(null);

        // Defer queue start so the accepting click cannot fall through onto PLAY
        // and re-open this dialog in the same gesture.
        window.setTimeout(() => {
            ouroCodeAcceptLockRef.current = false;
            if (nextMode) {
                guardQueueStartWithCustomMapConfirmation(nextMode);
            }
        }, 0);
    };

    const handleCloseOuroCode = () => {
        setOuroCodeDialogOpen(false);
        pendingOuroCodeModeRef.current = null;
        setPendingOuroCodeMode(null);
        ouroCodeAcceptLockRef.current = false;
    };

    const guardQueueStartWithCustomMapConfirmation = (mode) => {
        const unconfirmedCustomMaps = getUnconfirmedCustomWorkshopMaps();
        if (!unconfirmedCustomMaps.length) {
            startQueueByMode(mode);
            return;
        }

        setCustomMapSubscriptionDialog({
            open: true,
            maps: unconfirmedCustomMaps,
        });
        setPendingQueueStartMode(mode);
    };

    const handleDismissCustomMapSubscriptionDialog = () => {
        setCustomMapSubscriptionDialog({
            open: false,
            maps: [],
        });
        setPendingQueueStartMode(null);
    };

    const handleConfirmCustomMapSubscription = () => {
        const workshopIdsToConfirm = customMapSubscriptionDialog.maps
            .map((map) => String(map.workshopItemId || "").trim())
            .filter(Boolean);
        if (workshopIdsToConfirm.length > 0) {
            const merged = new Set([
                ...readConfirmedCustomMapWorkshopIds(),
                ...workshopIdsToConfirm,
            ]);
            writeConfirmedCustomMapWorkshopIds([...merged]);
        }

        const nextMode = pendingQueueStartMode;
        setCustomMapSubscriptionDialog({
            open: false,
            maps: [],
        });
        setPendingQueueStartMode(null);

        if (nextMode) {
            startQueueByMode(nextMode);
        }
    };

    const handleSendRequestFriend = (steamId) => {
        if (notifyIfCannotSendFriendRequest(user)) {
            return;
        }

        if (!resolvedUserSteamId) {
            console.warn(
                "[LobbyPage] Cannot send a friend request without a Steam ID",
                {
                    userId: user?.userId || null,
                    targetSteamId: steamId || null,
                },
            );
            return;
        }

        if (!steamId) {
            return;
        }

        actions.addPendingRequestLocally(steamId, resolvedUserSteamId);

        actions.sendFriendRequest({
            recipientSteamId: steamId,
            userId: user.userId,
            userSteamId: resolvedUserSteamId,
        });
    };

    const handleCancelConfirmDialog = () => {
        actions.closeConfirmDialog();
    };

    const handleCloseQueueBlockedModal = () => {
        actions.hideQueueBlockedModal();
    };

    const handleCancelInvite = (pendingInvite) => {
        const toUserId = pendingInvite?.userId;
        if (!toUserId) return;
        sendOwnerAction({
            messageId: "invite-cancel",
            toUserId,
        });
    };

    let friendsOnlineCount = 0;
    friends.forEach((friend) => {
        if (friend?.friendData?.isOnline) {
            friendsOnlineCount++;
        }
    });

    const copyTextToClipboard = async (text) => {
        try {
            const normalizedText = String(text || "").trim();
            if (!normalizedText) return false;

            const canTryClipboard =
                typeof navigator !== "undefined" &&
                navigator.clipboard &&
                typeof navigator.clipboard.writeText === "function";

            const legacyCopy = () => {
                const el = document.createElement("textarea");
                el.value = normalizedText;
                el.setAttribute("readonly", "");
                el.setAttribute("inputmode", "none");
                el.setAttribute("aria-hidden", "true");
                el.tabIndex = -1;
                el.style.position = "fixed";
                el.style.left = "-9999px";
                el.style.top = "0";
                el.style.opacity = "0";
                document.body.appendChild(el);
                el.focus({ preventScroll: true });
                el.select();
                el.setSelectionRange(0, el.value.length);
                const ok = document.execCommand("copy");
                el.blur();
                document.body.removeChild(el);
                if (
                    typeof document !== "undefined" &&
                    document.activeElement &&
                    typeof document.activeElement.blur === "function"
                ) {
                    document.activeElement.blur();
                }
                return ok;
            };

            // Prefer modern clipboard, but fall back if it throws (permissions/gesture quirks).
            if (canTryClipboard) {
                try {
                    await navigator.clipboard.writeText(normalizedText);
                } catch (e) {
                    if (!legacyCopy()) throw e;
                }
            } else if (!legacyCopy()) {
                throw new Error("Copy failed");
            }
            return true;
        } catch (err) {
            return false;
        }
    };

    const handleCopyLobbyCode = async (code) => {
        const ok = await copyTextToClipboard(code);
        if (ok) {
            pushToast({ type: "success", message: "Lobby code copied" });
        } else {
            pushToast({ type: "error", message: "Could not copy lobby code" });
            console.error("Failed to copy lobby code");
        }
    };

    const handleCopyReferralLink = async () => {
        const ok = await copyTextToClipboard(buildReferralInviteLink(referralCode));
        if (ok) {
            setReferralCopied(true);
            setTimeout(() => setReferralCopied(false), 1500);
        } else {
            console.error("Failed to copy referral invite link");
        }
    };

    const isBanned = String(accountStatus || "").trim().toLowerCase() === "banned";

    const handleCreateParty = async () => {
        if (isBanned || currentLobbyCode) return;
        const userId = user?.userId || user?._id || null;
        if (!userId || typeof sendOwnerAction !== "function") return;
        try {
            await sendOwnerAction({
                messageId: "create-solo-lobby",
                userId,
            });
        } catch (e) {
            console.error("LobbyPage: create-solo-lobby failed", e);
        }
    };

    const handleJoinParty = () => {
        if (currentLobbyCode) return;
        actions?.openJoinLobbyDialog?.();
    };

    const handleExitLobby = () => {
        actions.closeConfirmDialog();
        actions.leaveLobby();
        sendOwnerAction({
            messageId: "leave-lobby",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
        });
        // Clear invite code state when exiting lobby
        setInviteCode(null);
        setInviteExpiresAt(null);
        toast.info("You left the lobby", { durationMs: 4000 });
    };

    const handleKickPlayerFromLobby = (player) => {
        actions.kickPlayerFromLobby(player.userId);
        sendOwnerAction({
            messageId: "kick-player-from-lobby",
            lobbyCode: currentLobbyCode,
            playerToKickId: player.userId,
            clientId,
        });
        toast.info(`Kicked ${player?.nickname || "player"} from the lobby`, {
            durationMs: 4000,
        });
    };

    const isAccountAllowedToPlay = ["active", "verified"].includes(accountStatus);
    const showEmptyQueueHint =
        isAccountAllowedToPlay &&
        !isQueueEffectivelyClosed &&
        lobbyState === "standby" &&
        (catalog.platformStats?.playersSearching ?? null) === 0;
    // Keep queue estimate disabled until it is an approved standby signal.
    const showQueueEstimate = false;

    const startSoloMatchmaking = () => {
        if (!hasRequiredQueueSelections) {
            actions.showQueueErrorMessage(
                "Select at least one map and one server location before playing.",
            );
            return;
        }
        guardQueueStartWithOuroCode("solo");
    };

    const startLobbyMatchmakingByLeader = () => {
        if (currentLobbyCode && lobbyMembers.length > 1) {
            const offlineMembers = listOfflinePartyMembers({
                members: lobbyMembers,
                currentUserId: user?.userId,
                friends,
            });
            if (offlineMembers.length > 0) {
                toast.warning(buildOfflinePartyQueueToast(offlineMembers), {
                    durationMs: 4000,
                });
                return;
            }
        }
        if (!hasRequiredQueueSelections) {
            actions.showQueueErrorMessage(
                "Select at least one map and one server location before playing.",
            );
            return;
        }
        guardQueueStartWithOuroCode("lobby");
    };

    async function handleGenerateInviteCode() {
        try {
            if (!basePath) return;
            if (!user?.userId) return;
            if (!currentLobbyCode) return;

            setInviteLoading(true);

            const resp = await fetch(`${basePath}/api/v1/lobby/invite-code`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ttlMinutes: 15 }),
            });
            const data = await resp.json();
            if (!resp.ok || data?.ok !== true) {
                throw new Error(
                    data?.message || data?.error || "Failed to generate invite code",
                );
            }

            const nextCode = String(data.inviteCode || "").trim();
            setInviteCode(nextCode || null);
            setInviteExpiresAt(data.expiresAt || null);
            // Do not auto-copy: clipboard fallback focuses a textarea and opens
            // the mobile keyboard when this runs on lobby mount.
        } catch (e) {
            console.error("Failed to generate invite code:", e);
        } finally {
            setInviteLoading(false);
        }
    }

    return (
        <Box
            className="root"
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                flex: 1,
                width: "100%",
                boxSizing: "border-box",
                overflowX: "hidden",
                "@media (min-width: 1024px)": {
                    marginTop: "-82px",
                    paddingTop: "82px",
                },
            }}
        >
            <Box
                className="homepageRoot"
                sx={{
                    width: "100%",
                    marginTop: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflowX: "hidden",
                    boxSizing: "border-box",
                }}
            >
                {String(accountStatus || "").trim().toLowerCase() === "verified" ? (
                    <RefragPromoNotice dismissible />
                ) : null}

                <LobbyMatchNightProvider>
                <LobbyAlerts
                    queueClosedInfo={queueClosedInfo}
                    discordInviteUrl={discordInviteUrl}
                    scheduleCountdown={scheduleCountdown}
                    user={user}
                    actions={actions}
                    founderProgress={founderProgress}
                    referralCode={referralCode}
                    onCopyReferralLink={handleCopyReferralLink}
                    lobbyNoticeRefreshKey={lobbyNoticeRefreshKey}
                    isAccountAllowedToPlay={isAccountAllowedToPlay}
                />

                <LobbyMatchNightPanel />

                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Paper
                        className="lobbyFrame"
                        sx={{
                            width: "100%",
                            margin: "0",
                            textAlign: "center",
                            position: "relative",
                            minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
                            display: "flex",
                            flexDirection: "column",
                            transition: "transform 0.3s ease-in-out, margin-right 0.3s ease-in-out",
                            backgroundColor: "transparent",
                            boxSizing: "border-box",
                            border: "none",
                            borderRadius: "6px",
                            overflow:
                                isLockedMatchView || isStatusOverlayView
                                    ? "hidden"
                                    : "visible",
                            [MOBILE_NAV_MQ]: {
                                minHeight: "auto",
                                overflow: isLockedMatchView
                                    ? "hidden"
                                    : "visible",
                            },
                            [SHORT_VIEWPORT_MQ]: {
                                minHeight: "auto",
                                overflow: isLockedMatchView
                                    ? "hidden"
                                    : "visible",
                            },
                        }}
                        elevation={0}
                    >
                        {!user && <Typography>Please wait...</Typography>}

                        {user && (
                            <>
                                <Box
                                    className="lobbyContainer"
                                    sx={{
                                        position: "relative",
                                        display: "flex",
                                        justifyContent: "center",
                                        flexDirection: "column",
                                        alignItems: isLockedMatchView
                                            ? "stretch"
                                            : "center",
                                        flex: "1 0 auto",
                                        width: "100%",
                                        minHeight: "100%",
                                        margin: "0 auto",
                                        border: `1px solid ${colors.border.subtle}`,
                                        borderRadius: "6px",
                                        boxSizing: "border-box",
                                        perspective: isLockedMatchView
                                            ? "none"
                                            : "1200px",
                                        transformStyle: isLockedMatchView
                                            ? "flat"
                                            : "preserve-3d",
                                        background: colors.gradient.secondary,
                                        backdropFilter: isLockedMatchView
                                            ? "none"
                                            : "blur(4px)",
                                        // Clip match-ready / map-vote content to the rounded lobby frame.
                                        // In-flow content still grows the box with height: auto.
                                        overflow: isLockedMatchView
                                            ? "hidden"
                                            : isStatusOverlayView
                                              ? "hidden"
                                              : "visible",
                                        height: isLockedMatchView ? "auto" : undefined,
                                        isolation: isLockedMatchView
                                            ? "isolate"
                                            : undefined,
                                        [MOBILE_NAV_MQ]: {
                                            borderRadius: "6px",
                                            flex: "0 0 auto",
                                            minHeight: isStatusOverlayView
                                                ? `calc(${MAIN_SECTION_MIN_HEIGHT_SX.xs} - ${MOBILE_NAV_HEIGHT})`
                                                : "auto",
                                            height: "auto",
                                            // Stretch party rows to frame width (center shrink-wraps cards).
                                            alignItems: "stretch",
                                            justifyContent: "flex-start",
                                            gap: isSearchingView
                                                ? "0.35rem"
                                                : isLockedMatchView
                                                  ? 0
                                                  : "0.85rem",
                                            overflow: isLockedMatchView
                                                ? "hidden"
                                                : "visible",
                                            overflowX: "hidden",
                                            paddingTop: isSearchingView ? "3.6rem" : 0,
                                            paddingBottom: isSearchingView
                                                ? "0.75rem"
                                                : isLockedMatchView
                                                  ? "0.55rem"
                                                  : "0.35rem",
                                        },
                                        "@media (min-width: 769px)": {
                                            justifyContent:
                                                isLockedMatchView
                                                    ? "flex-start"
                                                    : isStatusOverlayView
                                                      ? "center"
                                                      : "flex-start",
                                        },
                                        [SHORT_VIEWPORT_MQ]: {
                                            flex: "0 0 auto",
                                            minHeight: "auto",
                                            height: "auto",
                                            alignItems: "stretch",
                                            justifyContent: "flex-start",
                                            gap: isSearchingView
                                                ? "0.3rem"
                                                : isLockedMatchView
                                                  ? 0
                                                  : "0.65rem",
                                            overflow: isLockedMatchView
                                                ? "hidden"
                                                : "visible",
                                            overflowX: "hidden",
                                            paddingTop: isSearchingView ? "3.35rem" : 0,
                                            paddingBottom: isSearchingView
                                                ? "0.65rem"
                                                : isLockedMatchView
                                                  ? "0.45rem"
                                                  : "0.25rem",
                                        },
                                    }}
                                >
                                    <MotionBox
                                        variants={SEARCHING_BRACKET_ANIMATION}
                                        initial="initial"
                                        animate={isStatusOverlayView ? "animate" : "initial"}
                                        transition={{
                                            ...SEARCHING_BRACKET_ANIMATION.transition,
                                            delay: 0,
                                        }}
                                        sx={{
                                            ...CORNER_BRACKET_SX,
                                            ...CORNER_BRACKET_TL_SX,
                                            zIndex: 9,
                                            [MOBILE_NAV_MQ]: { display: "none" },
                                        }}
                                    />
                                    <MotionBox
                                        variants={SEARCHING_BRACKET_ANIMATION}
                                        initial="initial"
                                        animate={isStatusOverlayView ? "animate" : "initial"}
                                        transition={{
                                            ...SEARCHING_BRACKET_ANIMATION.transition,
                                            delay: 0.25,
                                        }}
                                        sx={{
                                            ...CORNER_BRACKET_SX,
                                            ...CORNER_BRACKET_TR_SX,
                                            zIndex: 9,
                                            [MOBILE_NAV_MQ]: { display: "none" },
                                        }}
                                    />
                                    <MotionBox
                                        variants={SEARCHING_BRACKET_ANIMATION}
                                        initial="initial"
                                        animate={isStatusOverlayView ? "animate" : "initial"}
                                        transition={{
                                            ...SEARCHING_BRACKET_ANIMATION.transition,
                                            delay: 0.75,
                                        }}
                                        sx={{
                                            ...CORNER_BRACKET_SX,
                                            ...CORNER_BRACKET_BL_SX,
                                            zIndex: 9,
                                            [MOBILE_NAV_MQ]: { display: "none" },
                                        }}
                                    />
                                    <MotionBox
                                        variants={SEARCHING_BRACKET_ANIMATION}
                                        initial="initial"
                                        animate={isStatusOverlayView ? "animate" : "initial"}
                                        transition={{
                                            ...SEARCHING_BRACKET_ANIMATION.transition,
                                            delay: 0.5,
                                        }}
                                        sx={{
                                            ...CORNER_BRACKET_SX,
                                            ...CORNER_BRACKET_BR_SX,
                                            zIndex: 9,
                                            [MOBILE_NAV_MQ]: { display: "none" },
                                        }}
                                    />
                                    
                                    <LobbyParticles />

                                    {!currentLobbyCode && !isStatusOverlayView && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: { xs: "0.7rem", xl: "1rem" },
                                                right: { xs: "0.7rem", xl: "1rem" },
                                                zIndex: 11,
                                                [MOBILE_NAV_MQ]: {
                                                    display: isStatusOverlayView
                                                        ? "none"
                                                        : "block",
                                                    top: "0.7rem",
                                                    right: "0.7rem",
                                                },
                                            }}
                                        >
                                            <LobbyMatchNightShowButton />
                                        </Box>
                                    )}

                                    {isStatusOverlayView && (
                                        <MotionBox
                                            initial={{
                                                opacity: 0,
                                            }}
                                            animate={{
                                                opacity: 1,
                                            }}
                                            exit={{
                                                opacity: 0,
                                            }}
                                            transition={{
                                                duration: 0.6,
                                                ease: [0.22, 1, 0.36, 1],
                                            }}
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                zIndex: 2,
                                                pointerEvents: "none",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {/* cinematic darkening */}
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    background: `
                                                    radial-gradient(
                                                        circle at center,
                                                        rgba(0,0,0,0.16) 0%,
                                                        rgba(0,0,0,0.52) 100%
                                                    )
                                                `,
                                                }}
                                            />

                                            {/* tactical amber atmosphere */}
                                            <MotionBox
                                                animate={{
                                                    opacity: [0.18, 0.28, 0.18],
                                                    scale: [1, 1.04, 1],
                                                }}
                                                transition={{
                                                    repeat: Infinity,
                                                    duration: 5,
                                                    ease: "easeInOut",
                                                }}
                                                sx={{
                                                    position: "absolute",
                                                    left: "50%",
                                                    top: "45%",
                                                    transform: "translate(-50%, -50%)",
                                                    width: "58rem",
                                                    height: "58rem",
                                                    borderRadius: "50%",
                                                    background: `
                                                        radial-gradient(
                                                            circle,
                                                            rgba(232,163,61,0.14) 0%,
                                                            rgba(232,163,61,0.05) 32%,
                                                            transparent 72%
                                                        )
                                                    `,
                                                    filter: "blur(80px)",
                                                }}
                                            />

                                            {/* subtle scan lines */}
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    opacity: 0.03,
                                                    backgroundImage: `
                                                        linear-gradient(
                                                            transparent 0%,
                                                            rgba(255,255,255,0.08) 50%,
                                                            transparent 100%
                                                        )
                                                    `,
                                                    backgroundSize: "100% 4px",
                                                    mixBlendMode: "soft-light",
                                                }}
                                            />
                                        </MotionBox>
                                    )}

                                    {/* Create / Join party — mobile only, and only when not in a lobby */}
                                    {!currentLobbyCode && (
                                        <Box
                                            className="lobbyPartyActions"
                                            sx={{
                                                display: "none",
                                                [MOBILE_NAV_MQ]: {
                                                    display: isStatusOverlayView
                                                        ? "none"
                                                        : "flex",
                                                    position: "absolute",
                                                    top: "0.7rem",
                                                    left: "0.7rem",
                                                    zIndex: 11,
                                                    alignItems: "center",
                                                    gap: "0.4rem",
                                                },
                                            }}
                                        >
                                            <TooltipComponent
                                                title={
                                                    isBanned
                                                        ? "Unavailable while banned"
                                                        : "Create party"
                                                }
                                                placement="bottom"
                                                enterTouchDelay={0}
                                                leaveTouchDelay={2500}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        opacity: isBanned ? 0.42 : 1,
                                                    }}
                                                >
                                                    <TopBarActionButton
                                                        aria-label="Create party"
                                                        onClick={handleCreateParty}
                                                    >
                                                        <GroupAddOutlined
                                                            sx={TOPBAR_ACTION_ICON_SX}
                                                        />
                                                    </TopBarActionButton>
                                                </Box>
                                            </TooltipComponent>
                                            <TooltipComponent
                                                title="Join party"
                                                placement="bottom"
                                                enterTouchDelay={0}
                                                leaveTouchDelay={2500}
                                            >
                                                <Box sx={{ display: "inline-flex" }}>
                                                    <TopBarActionButton
                                                        aria-label="Join party"
                                                        onClick={handleJoinParty}
                                                    >
                                                        <LoginOutlined
                                                            sx={TOPBAR_ACTION_ICON_SX}
                                                        />
                                                    </TopBarActionButton>
                                                </Box>
                                            </TooltipComponent>
                                        </Box>
                                    )}

                                    {/* Invite Code (top-left) and Exit (top-right), same row.
                                        Show whenever we have a lobby — inviteCode may load after
                                        reload (especially while lobby status is "searching").
                                        Hide during match-found / map vote so they don't sit over the overlay. */}
                                    {currentLobbyCode && !isLockedMatchView && (
                                        <Box
                                            className="containerLobbyCodeFriendButton"
                                            sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                alignItems: "center",
                                                position: "absolute",
                                                top: { xs: "0.6rem", xl: "1rem" },
                                                left: { xs: "0.6rem", xl: "1rem" },
                                                right: { xs: "0.6rem", xl: "1rem" },
                                                zIndex: 11,
                                                [MOBILE_NAV_MQ]: {
                                                    top: "1.1rem",
                                                    left: "0.7rem",
                                                    right: "0.7rem",
                                                },
                                            }}
                                        >
                                            <Box
                                                className="containerLobbyCode"
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    width: "100%",
                                                    gap: "1rem",
                                                    [MOBILE_NAV_MQ]: {
                                                        position: "relative",
                                                        alignItems: "center",
                                                        gap: "0.5rem",
                                                        minHeight: 0,
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "flex-start",
                                                        justifyContent: "center",
                                                        gap: "0.2rem",
                                                        minWidth: 0,
                                                        [MOBILE_NAV_MQ]: {
                                                            flex: "1 1 auto",
                                                            overflow: "hidden",
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: {
                                                                xs: "0.62rem",
                                                                xl: "0.68rem",
                                                            },
                                                            fontWeight: 600,
                                                            letterSpacing: "0.14em",
                                                            textTransform: "uppercase",
                                                            color: "rgba(255,255,255,0.42)",
                                                            lineHeight: 1,
                                                            userSelect: "none",
                                                            [MOBILE_NAV_MQ]: {
                                                                fontSize: "0.58rem",
                                                            },
                                                        }}
                                                    >
                                                        Lobby Code
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            alignItems: "center",
                                                            gap: "0.55rem",
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Typography
                                                            component="button"
                                                            type="button"
                                                            onClick={() =>
                                                                handleCopyLobbyCode(displayLobbyCode)
                                                            }
                                                            sx={{
                                                                all: "unset",
                                                                cursor: "pointer",
                                                                fontFamily: "'JetBrains Mono', monospace",
                                                                fontSize: {
                                                                    xs: "0.95rem",
                                                                    xl: "1.05rem",
                                                                },
                                                                fontWeight: 700,
                                                                letterSpacing: "0.08em",
                                                                color: colors.text.primary,
                                                                lineHeight: 1.1,
                                                                userSelect: "none",
                                                                whiteSpace: "nowrap",
                                                                [MOBILE_NAV_MQ]: {
                                                                    fontSize: "0.82rem",
                                                                    letterSpacing: "0.06em",
                                                                },
                                                            }}
                                                        >
                                                            {maskInviteCode(displayLobbyCode)}
                                                        </Typography>
                                                        <IconButton
                                                            aria-label="Copy lobby code"
                                                            onClick={() =>
                                                                handleCopyLobbyCode(displayLobbyCode)
                                                            }
                                                            sx={{
                                                                width: {
                                                                    xs: "1.55rem",
                                                                    xl: "1.7rem",
                                                                },
                                                                height: {
                                                                    xs: "1.55rem",
                                                                    xl: "1.7rem",
                                                                },
                                                                padding: 0,
                                                                borderRadius: "8px",
                                                                color: "rgba(255,255,255,0.72)",
                                                                background: "transparent",
                                                                border: "1px solid rgba(255,255,255,0.14)",
                                                                transition:
                                                                    "border-color 0.15s ease-in-out, color 0.15s ease-in-out, background-color 0.15s ease-in-out",
                                                                [MOBILE_NAV_MQ]: {
                                                                    width: "1.35rem",
                                                                    height: "1.35rem",
                                                                    borderRadius: "7px",
                                                                },
                                                                "&:hover": {
                                                                    backgroundColor: "rgba(255,255,255,0.05)",
                                                                    borderColor: "rgba(255,255,255,0.28)",
                                                                    color: colors.text.primary,
                                                                },
                                                            }}
                                                        >
                                                            <CopyAllOutlined
                                                                sx={{
                                                                    fontSize: {
                                                                        xs: "0.9rem",
                                                                        xl: "0.95rem",
                                                                    },
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: {
                                                            xs: "0.45rem",
                                                            xl: "0.6rem",
                                                        },
                                                        flexShrink: 0,
                                                        [MOBILE_NAV_MQ]: {
                                                            flexShrink: 0,
                                                            position: "relative",
                                                            zIndex: 1,
                                                            alignSelf: "center",
                                                            alignItems: "center",
                                                        },
                                                    }}
                                                >
                                                    {!isStatusOverlayView ? (
                                                        <LobbyMatchNightShowButton />
                                                    ) : null}
                                                    <Button
                                                        variant="contained"
                                                        disableElevation
                                                        sx={[
                                                            LOBBY_EXIT_BUTTON_SX,
                                                            shouldBlockActions() && LOBBY_EXIT_BUTTON_BLOCKED_SX,
                                                        ]}
                                                        onClick={() =>
                                                            shouldBlockActions()
                                                                ? actions.showUnavailableActionMessage()
                                                                : actions.openConfirmDialog({
                                                                    which: "exitLobby",
                                                                })
                                                        }
                                                    >
                                                        Leave lobby
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}

                                    <MotionBox
                                        initial={false}
                                        animate={
                                            isStatusOverlayView
                                                ? {
                                                      opacity: 0,
                                                      y: 28,
                                                      scale: 0.97,
                                                  }
                                                : {
                                                      opacity: 1,
                                                      y: 0,
                                                      scale: 1,
                                                  }
                                        }
                                        transition={{
                                            duration: 0.15,
                                            ease: "easeInOut",
                                        }}
                                        sx={{
                                            // Only clip while collapsing — standby maxHeight/overflow
                                            // was cutting party cards off at the bottom.
                                            overflow: isStatusOverlayView
                                                ? "hidden"
                                                : "visible",
                                            maxHeight: isStatusOverlayView
                                                ? 0
                                                : "none",
                                            transition: "max-height 0.15s ease-in-out",
                                            pointerEvents: isStatusOverlayView
                                                ? "none"
                                                : "auto",
                                            transformOrigin: "center bottom",
                                            // Mobile search UI is in-flow — drop the stage entirely
                                            // so absolute overlays cannot trap height at ~0.
                                            [MOBILE_NAV_MQ]: isStatusOverlayView
                                                ? {
                                                      display: "none",
                                                  }
                                                : {
                                                      width: "100%",
                                                      alignSelf: "stretch",
                                                  },
                                            [SHORT_VIEWPORT_MQ]: isStatusOverlayView
                                                ? {
                                                      display: "none",
                                                  }
                                                : {
                                                      width: "100%",
                                                      alignSelf: "stretch",
                                                  },
                                        }}
                                    >
                                        <LobbyPlayerStage
                                            actions={actions}
                                            avatarCacheBust={catalog?.avatarCacheBust || 0}
                                            canFlipCards={canFlipCards()}
                                            currentLobbyCode={currentLobbyCode}
                                            friendlist={friends}
                                            friendsOnlineCount={friendsOnlineCount}
                                            handleCancelInvite={handleCancelInvite}
                                            handleKickPlayerFromLobby={handleKickPlayerFromLobby}
                                            handleSendRequestFriend={handleSendRequestFriend}
                                            lobbyState={lobbyState}
                                            otherMembers={otherMembers}
                                            pendingBySlot={pendingBySlot}
                                            pendingRequests={pendingRequests}
                                            selfMember={selfMember}
                                            user={user}
                                        />
                                    </MotionBox>

                                    <AnimatePresence mode="wait">
                                        {isSearchingView && (
                                            <MotionBox
                                                key="lobby-searching-status"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                                transition={{
                                                    duration: 0.15,
                                                    ease: "easeInOut",
                                                }}
                                                sx={{
                                                    // Desktop: overlay the tall lobby frame.
                                                    // Mobile: in-flow — grow to fill the frame so the
                                                    // info bar/tips sit at the bottom.
                                                    position: "absolute",
                                                    inset: 0,
                                                    zIndex: 8,
                                                    display: "flex",
                                                    alignItems: "stretch",
                                                    justifyContent: "stretch",
                                                    pointerEvents: "none",
                                                    pt: {
                                                        xs: "0.5rem",
                                                        xl: "0.65rem",
                                                    },
                                                    pb: {
                                                        xs: "1rem",
                                                        xl: "1.15rem",
                                                    },
                                                    [MOBILE_NAV_MQ]: {
                                                        position: "relative",
                                                        inset: "auto",
                                                        width: "100%",
                                                        flex: "1 1 auto",
                                                        alignItems: "stretch",
                                                        justifyContent: "flex-start",
                                                        pointerEvents: "auto",
                                                        pt: 0,
                                                        pb: "0.15rem",
                                                        px: 0,
                                                        minHeight: 0,
                                                    },
                                                    [SHORT_VIEWPORT_MQ]: {
                                                        position: "relative",
                                                        inset: "auto",
                                                        width: "100%",
                                                        flex: "1 1 auto",
                                                        alignItems: "stretch",
                                                        justifyContent: "flex-start",
                                                        pointerEvents: "auto",
                                                        pt: 0,
                                                        pb: "0.1rem",
                                                        px: 0,
                                                        minHeight: 0,
                                                    },
                                                }}
                                            >
                                                <LobbySearchingStatus
                                                    timestamp={
                                                        user?.playerQueueActionStartTs
                                                    }
                                                    onCancel={handleStopSearch}
                                                    canCancel={canCancelSearch}
                                                    isParty={isSearchingParty}
                                                    members={lobbyMembers}
                                                    maxPlayers={
                                                        currentLobby?.maxPlayers || 5
                                                    }
                                                    regionsLabel={
                                                        serverLocationsRecapSummary?.text ||
                                                        "All regions"
                                                    }
                                                    averageElo={searchingAverageElo}
                                                    playersSearching={
                                                        catalog.platformStats
                                                            ?.playersSearching ?? null
                                                    }
                                                    serversOnline={
                                                        catalog.platformStats
                                                            ?.serversOnline ?? null
                                                    }
                                                    compatibleCount={
                                                        queueEstimate?.compatibleCount ??
                                                        null
                                                    }
                                                    mapsLabel={
                                                        mapsRecapSummary?.text ||
                                                        "All maps"
                                                    }
                                                    smallerMatchOffer={
                                                        queueStatus?.smallerMatchOffer || null
                                                    }
                                                    canActOnSmallerMatch={
                                                        canCancelSearch
                                                    }
                                                    onSmallerMatchOptIn={
                                                        handleSmallerMatchOptIn
                                                    }
                                                    onSmallerMatchDecline={
                                                        handleSmallerMatchDecline
                                                    }
                                                />
                                            </MotionBox>
                                        )}
                                        {isMatchReadyView && (
                                            <MotionBox
                                                key="lobby-match-ready-status"
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{
                                                    duration: 0.15,
                                                    ease: "easeInOut",
                                                }}
                                                sx={{
                                                    position: "relative",
                                                    zIndex: 8,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignSelf: "stretch",
                                                    width: "100%",
                                                    maxWidth: "none",
                                                    flex: "1 1 auto",
                                                    minHeight: 0,
                                                    margin: 0,
                                                    padding: 0,
                                                    pointerEvents: "auto",
                                                    pt: 0,
                                                    pb: {
                                                        xs: "0.75rem",
                                                        xl: "1rem",
                                                    },
                                                    boxSizing: "border-box",
                                                    [MOBILE_NAV_MQ]: {
                                                        pb: "0.45rem",
                                                        px: 0,
                                                        minHeight: "auto",
                                                    },
                                                    [SHORT_VIEWPORT_MQ]: {
                                                        pb: "0.3rem",
                                                        px: 0,
                                                        minHeight: "auto",
                                                    },
                                                }}
                                            >
                                                <LobbyMatchReadyStatus
                                                    matchAcceptance={matchAcceptDialogState}
                                                    onAcceptMatch={onAcceptMatch}
                                                    partyMembers={lobbyMembers}
                                                    selfUser={user}
                                                    avatarCacheBust={
                                                        catalog?.avatarCacheBust || 0
                                                    }
                                                    regionsLabel={
                                                        serverLocationsRecapSummary?.text ||
                                                        "All Regions"
                                                    }
                                                    serverLocations={
                                                        catalog?.serverLocations || []
                                                    }
                                                />
                                            </MotionBox>
                                        )}
                                        {isMapVoteView && (
                                            <MotionBox
                                                key="lobby-map-vote-status"
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{
                                                    duration: 0.15,
                                                    ease: "easeInOut",
                                                }}
                                                sx={{
                                                    position: "relative",
                                                    zIndex: 8,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignSelf: "stretch",
                                                    width: "100%",
                                                    maxWidth: "none",
                                                    flex: "1 1 auto",
                                                    minHeight: 0,
                                                    margin: 0,
                                                    padding: 0,
                                                    pointerEvents: "auto",
                                                    pt: 0,
                                                    pb: {
                                                        xs: "0.75rem",
                                                        xl: "1rem",
                                                    },
                                                    boxSizing: "border-box",
                                                    [MOBILE_NAV_MQ]: {
                                                        pb: "0.45rem",
                                                        px: 0,
                                                        minHeight: "auto",
                                                    },
                                                    [SHORT_VIEWPORT_MQ]: {
                                                        pb: "0.3rem",
                                                        px: 0,
                                                        minHeight: "auto",
                                                    },
                                                }}
                                            >
                                                <LobbyMapVoteStatus
                                                    matchId={mapVoteState.matchId}
                                                    candidates={mapVoteState.candidates}
                                                    blockedMaps={mapVoteState.blockedMaps}
                                                    expiresAt={mapVoteState.expiresAt}
                                                    durationSeconds={
                                                        mapVoteState.durationSeconds
                                                    }
                                                    voters={mapVoteState.voters}
                                                    votes={mapVoteState.votes}
                                                    myVote={mapVoteState.myVote}
                                                    resolvedMap={mapVoteState.resolvedMap}
                                                    wasTie={mapVoteState.wasTie}
                                                    tieBrokenBy={mapVoteState.tieBrokenBy}
                                                    topCount={mapVoteState.topCount}
                                                    currentUserId={user?.userId}
                                                    partyUserIds={partyMemberUserIds}
                                                    onVote={onVoteMap}
                                                    onDismiss={onDismissMapVote}
                                                />
                                            </MotionBox>
                                        )}
                                    </AnimatePresence>

                                    <LobbyMatchmakingDock
                                        isSearchingView={isStatusOverlayView}
                                        lobbyState={lobbyState}
                                        matchmakingStarted={matchmakingStarted}
                                        isLeader={isLeader}
                                        hasRequiredQueueSelections={hasRequiredQueueSelections}
                                        currentLobbyCode={currentLobbyCode}
                                        hasSelectedMaps={hasSelectedMaps}
                                        hasSelectedServerLocations={hasSelectedServerLocations}
                                        startLobbyMatchmakingByLeader={startLobbyMatchmakingByLeader}
                                        isAccountAllowedToPlay={isAccountAllowedToPlay}
                                        isQueueEffectivelyClosed={isQueueEffectivelyClosed}
                                        startSoloMatchmaking={startSoloMatchmaking}
                                        showEmptyQueueHint={showEmptyQueueHint}
                                        showQueueEstimate={showQueueEstimate}
                                        queueEstimate={queueEstimate}
                                        discordInviteUrl={discordInviteUrl}
                                        shouldBlockActions={shouldBlockActions}
                                        actions={actions}
                                        lobbyMapsToDisplay={lobbyMapsToDisplay}
                                        lobbyServerLocationsToDisplay={lobbyServerLocationsToDisplay}
                                        serverLocationsRecapSummary={serverLocationsRecapSummary}
                                        lobbyOptionsToDisplay={lobbyOptionsToDisplay}
                                        matchmakingRecapSummary={matchmakingRecapSummary}
                                        canEditLobbySettings={canEditLobbySettings}
                                        platformStats={catalog.platformStats}
                                        lobbyQueueMode={currentLobby?.queueMode || "standard"}
                                        lobbyMemberCount={lobbyMembers?.length || 1}
                                        onLobbyQueueModeSelect={handleLobbyQueueModeSelect}
                                        onOpenSettings={handleOpenLobbySettings}
                                    />

                                </Box>
                            </>
                        )}
                    </Paper>
                </Box>
                </LobbyMatchNightProvider>

                <ConfirmDialog
                    open={
                        catalog.confirmDialog &&
                        catalog.confirmDialog.open &&
                        catalog.confirmDialog.which === "exitLobby"
                    }
                    IconComponent={ExitToApp}
                    title="Exit Lobby"
                    message="Are you sure you want to exit the lobby?"
                    confirmText="Exit"
                    cancelText="Cancel"
                    onConfirm={handleExitLobby}
                    onCancel={handleCancelConfirmDialog}
                />

                <CustomMapSubscriptionDialog
                    maps={customMapSubscriptionDialog.maps}
                    onClose={handleDismissCustomMapSubscriptionDialog}
                    onConfirm={handleConfirmCustomMapSubscription}
                    open={customMapSubscriptionDialog.open}
                />

                <OuroCodeDialog
                    open={ouroCodeDialogOpen}
                    onAccept={handleAcceptOuroCode}
                    onClose={handleCloseOuroCode}
                />

                <SteamPrivacyCheckDialog
                    open={queueBlockedModal.open}
                    onClose={handleCloseQueueBlockedModal}
                    message={queueBlockedModal.message}
                    reason={queueBlockedModal.reason}
                    blockedMembers={queueBlockedModal.blockedMembers}
                    isParty={isSearchingParty}
                />

                <SmallerMatchIntroDialog
                    open={smallerMatchIntroOpen}
                    onClose={dismissSmallerMatchIntro}
                />

            </Box>
        </Box>
    )
}

LobbyPage.propTypes = {
    actions: PropTypes.object,
    catalog: PropTypes.object.isRequired,
};
